import { Link } from 'react-router';
import { Bed, Bath, Maximize, MapPin, Tag } from 'lucide-react';
import { Property } from '../data/mockData';
import { Button } from './ui/button';
import { ImageWithFallback } from './figma/ImageWithFallback';

interface PropertyCardProps {
  property: Property;
}

export function PropertyCard({ property }: PropertyCardProps) {
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('ar-EG', {
      style: 'currency',
      currency: 'EGP',
      minimumFractionDigits: 0,
    }).format(price);
  };

  return (
    <Link to={`/property/${property.id}`} className="block">
      <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300 h-full">
        {/* Image */}
        <div className="relative h-56 overflow-hidden group">
          <ImageWithFallback
            src={property.mainImage || `https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&h=600&fit=crop`}
            alt={property.title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          />
          
          {/* Badges */}
          <div className="absolute top-3 right-3 flex gap-2">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              property.type === 'rent' 
                ? 'bg-[#B8960C] text-white' 
                : 'bg-[#D4AF37] text-white'
            }`}>
              {property.type === 'rent' ? 'للإيجار' : 'للبيع'}
            </span>
            {property.featured && (
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#FFE5A0] text-[#8B7109]">
                مميز
              </span>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Category */}
          <div className="flex items-center gap-2 text-[#D4AF37] text-sm mb-2">
            <Tag className="w-4 h-4" />
            <span>{property.category}</span>
          </div>

          {/* Title */}
          <h3 className="font-bold text-lg text-gray-800 mb-2 line-clamp-1">
            {property.title}
          </h3>

          {/* Location */}
          <div className="flex items-center gap-2 text-gray-600 text-sm mb-3">
            <MapPin className="w-4 h-4" />
            <span className="line-clamp-1">{property.location}</span>
          </div>

          {/* Details */}
          <div className="flex items-center justify-between gap-3 text-gray-600 text-sm mb-4 pb-4 border-b">
            <div className="flex items-center gap-1">
              <Bed className="w-4 h-4" />
              <span>{property.bedrooms} غرفة</span>
            </div>
            <div className="flex items-center gap-1">
              <Bath className="w-4 h-4" />
              <span>{property.bathrooms} حمام</span>
            </div>
            <div className="flex items-center gap-1">
              <Maximize className="w-4 h-4" />
              <span>{property.area} م²</span>
            </div>
          </div>

          {/* Price and Button */}
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-[#8B7109]">
                {formatPrice(property.price)}
              </div>
            </div>
            <Button asChild className="bg-[#D4AF37] hover:bg-[#B8960C] text-white text-sm px-4">
              <span>عرض التفاصيل</span>
            </Button>
          </div>
        </div>
      </div>
    </Link>
  );
}